import { useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, RefreshCw, Share2, Trash2 } from 'lucide-react'
import { abrirJanelaPartilhar } from '@core/partilhas/partilhasEstado'
import { useAppStore } from '@core/store'
import { useAuth } from '@core/auth/useAuth'
import {
  abrirModeloPartilhado,
  apagarModeloPartilhado,
  definirPerguntarAoAbrir,
  definirQuotaModelos,
  perguntarAoAbrir,
  recarregarModelos,
  type EstadoModelos,
} from './modelosPartilhados'
import {
  agruparPorNome,
  formatarBytes,
  fraccaoOcupada,
  type ModeloPartilhado,
  textoDaOcupacao,
} from './modelosRegras'

function quando(iso: string): string {
  try {
    return new Date(iso).toLocaleString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

/** A lista "Modelos do workspace", na barra lateral.
 *
 *  Mostra o que os colegas partilharam (agrupado por nome, com as
 *  revisões), quanto espaço ocupa, e deixa abrir, apagar as próprias
 *  revisões e ordenar pelo que ocupa mais espaço, para se saber o que
 *  apagar quando o limite aperta. Abrir usa a cache do browser pelo
 *  SHA-256; só descarrega o que ainda cá não está. */
export default function ModelosDoWorkspace({ estado }: { estado: EstadoModelos }) {
  const models = useAppStore((s) => s.models)
  const { user, workspace } = useAuth()
  const [aberto, setAberto] = useState(true)
  const [ordem, setOrdem] = useState<'nome' | 'espaco'>('nome')
  const [expandidos, setExpandidos] = useState<Record<string, boolean>>({})
  const [perguntar, setPerguntar] = useState(perguntarAoAbrir)
  const [quotaMb, setQuotaMb] = useState('')

  const grupos = useMemo(
    () => agruparPorNome(estado.lista ?? [], ordem),
    [estado.lista, ordem],
  )
  const abertos = useMemo(
    () => new Set(models.map((m) => m.sha256.toLowerCase())),
    [models],
  )
  const servidor = estado.estado
  const total = estado.lista?.length ?? 0

  const linhaRevisao = (m: ModeloPartilhado, principal: boolean) => {
    const op = estado.operacoes[m.sha256]
    const jaAberto = abertos.has(m.sha256.toLowerCase())
    return (
      <div key={m.id} className="flex items-center gap-1">
        <div className="min-w-0 flex-1">
          <div
            className={`truncate ${principal ? 'text-sm font-medium text-[var(--color-text)]' : 'text-xs text-[var(--color-text-muted)]'}`}
            title={`${m.nome} · SHA-256 ${m.sha256}`}
          >
            {principal ? m.nome : `Revisão ${m.revisao}`}
          </div>
          <div className="truncate text-[10px] text-[var(--color-text-dim)]">
            {principal && `rev. ${m.revisao} · `}
            {formatarBytes(m.tamanho_comprimido)} no servidor ·{' '}
            {m.carregado_por_nome ?? m.carregado_por_email ?? 'autor desconhecido'},{' '}
            {quando(m.carregado_em)}
          </div>
          {principal && m.visivel_para && (
            <div className="truncate text-[10px] text-[var(--color-text-dim)]" title={m.visivel_para}>
              {m.visivel_para}
            </div>
          )}
          {op && (
            <div className="text-[10px] text-[var(--color-accent)]">
              A descarregar… {op.feito}/{op.total}
            </div>
          )}
        </div>
        {principal && m.partilha_id && (
          <button
            type="button"
            onClick={() =>
              abrirJanelaPartilhar({
                tipo: 'modelo',
                nome: m.nome,
                partilhaId: m.partilha_id ?? null,
                workspaceNome: workspace?.name,
                aoMudar: () => void recarregarModelos(),
              })
            }
            title={
              m.pode_gerir
                ? 'Com quem está partilhado este modelo (todas as revisões): acrescentar ou remover pessoas, ou retirar a partilha.'
                : 'Quem tem acesso a este modelo'
            }
            aria-label={`${m.pode_gerir ? 'Gerir a partilha de' : 'Acessos a'} ${m.nome}`}
            className="flex shrink-0 items-center gap-0.5 rounded border border-[var(--color-border)] px-1.5 py-0 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-accent)]"
          >
            <Share2 size={10} aria-hidden />
            {m.pode_gerir ? 'Gerir partilha' : 'Acessos'}
          </button>
        )}
        <button
          type="button"
          disabled={!!op}
          onClick={() => void abrirModeloPartilhado(m)}
          title={
            jaAberto
              ? 'Já está aberto: torna-o o modelo activo.'
              : 'Abrir este ficheiro no viewer. Usa a cache do browser se já foi aberto aqui; senão descarrega-o.'
          }
          className="rounded border border-[var(--color-border)] px-1.5 py-0 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-50"
        >
          {jaAberto ? 'Aberto' : 'Abrir'}
        </button>
        {m.pode_apagar && (
          <button
            type="button"
            onClick={() => void apagarModeloPartilhado(m)}
            title="Apagar do servidor esta revisão (só quem a carregou, ou um administrador). Fica 7 dias na Reciclagem, onde se pode restaurar. Para deixar de partilhar sem apagar, use Partilhar com."
            aria-label={`Apagar a revisão ${m.revisao} de ${m.nome}`}
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-[var(--color-text-dim)] hover:bg-[var(--color-error-bg)] hover:text-[var(--color-error-text)]"
          >
            <Trash2 size={11} />
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="border-b border-[var(--color-border)]">
      <div className="flex items-center justify-between px-3 py-1.5 text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
        <button
          type="button"
          onClick={() => setAberto((v) => !v)}
          className="flex items-center gap-1 uppercase"
          aria-expanded={aberto}
        >
          {aberto ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          Modelos ({total})
        </button>
        <button
          type="button"
          onClick={() => void recarregarModelos()}
          title="Atualizar a lista"
          aria-label="Atualizar a lista dos modelos do workspace"
          className="flex h-5 w-5 items-center justify-center rounded text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
        >
          <RefreshCw size={11} />
        </button>
      </div>

      {aberto && (
        <div className="flex max-h-72 flex-col gap-1.5 overflow-y-auto px-3 pb-2 text-xs">
          {servidor && !servidor.ativo && (
            <p className="text-[var(--color-warn-text)]">{servidor.motivo}</p>
          )}
          {estado.erro && (
            <p className="text-[var(--color-error-text)]">{estado.erro}</p>
          )}

          {servidor && (
            <div title="Os ficheiros ficam comprimidos no servidor; o limite conta o tamanho comprimido.">
              <div className="flex justify-between text-[10px] text-[var(--color-text-dim)]">
                <span data-campo="ocupacao">{textoDaOcupacao(servidor, formatarBytes)}</span>
                <span>máx. {formatarBytes(servidor.max_ficheiro_bytes)} por ficheiro</span>
              </div>
              {servidor.ocupacao !== 'minha' && (
              <div className="mt-0.5 h-1 overflow-hidden rounded bg-[var(--color-border)]">
                <div
                  className={`h-full ${fraccaoOcupada(servidor.usado_bytes, servidor.quota_bytes) > 0.9 ? 'bg-[var(--color-error-text)]' : 'bg-[var(--color-accent)]'}`}
                  style={{
                    width: `${Math.round(fraccaoOcupada(servidor.usado_bytes, servidor.quota_bytes) * 100)}%`,
                  }}
                />
              </div>
              )}
            </div>
          )}

          {total === 0 ? (
            <p className="text-[var(--color-text-dim)]">
              Ainda não há modelos partilhados. Quem abre um IFC pode escolher
              Partilhar no workspace; quem não o faz continua a abrir os seus
              ficheiros do disco, como sempre.
            </p>
          ) : (
            <>
              <label className="flex items-center gap-1 text-[10px] text-[var(--color-text-dim)]">
                <input
                  type="checkbox"
                  checked={ordem === 'espaco'}
                  onChange={(e) => setOrdem(e.target.checked ? 'espaco' : 'nome')}
                />
                Ordenar pelo espaço ocupado
              </label>
              <ul className="flex flex-col gap-1.5">
                {grupos.map((g) => {
                  const [ultima, ...anteriores] = g.revisoes
                  const exp = !!expandidos[g.chave]
                  return (
                    <li
                      key={g.chave}
                      className="rounded border border-[var(--color-border-subtle)] px-1.5 py-1"
                    >
                      {linhaRevisao(ultima, true)}
                      {anteriores.length > 0 && (
                        <>
                          <button
                            type="button"
                            onClick={() =>
                              setExpandidos((s) => ({ ...s, [g.chave]: !exp }))
                            }
                            className="mt-0.5 text-[10px] text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
                          >
                            {exp ? 'Esconder' : 'Ver'} {anteriores.length}{' '}
                            {anteriores.length === 1 ? 'revisão anterior' : 'revisões anteriores'}{' '}
                            ({formatarBytes(g.ocupado)} no total)
                          </button>
                          {exp && (
                            <div className="mt-1 flex flex-col gap-1 border-l border-[var(--color-border)] pl-2">
                              {anteriores.map((m) => linhaRevisao(m, false))}
                            </div>
                          )}
                        </>
                      )}
                    </li>
                  )
                })}
              </ul>
            </>
          )}

          <label className="flex items-center gap-1 text-[10px] text-[var(--color-text-dim)]">
            <input
              type="checkbox"
              checked={perguntar}
              onChange={(e) => {
                setPerguntar(e.target.checked)
                definirPerguntarAoAbrir(e.target.checked)
              }}
            />
            Perguntar se quero partilhar ao abrir um IFC
          </label>

          {user?.is_vn2r_admin && servidor && (
            <form
              className="flex items-center gap-1 text-[10px] text-[var(--color-text-dim)]"
              onSubmit={(e) => {
                e.preventDefault()
                const n = Number.parseInt(quotaMb, 10)
                void definirQuotaModelos(Number.isFinite(n) && n > 0 ? n : null)
              }}
            >
              <span>Limite do workspace (MB, administração):</span>
              <input
                value={quotaMb}
                onChange={(e) => setQuotaMb(e.target.value)}
                placeholder={String(Math.round(servidor.quota_bytes / 1048576))}
                inputMode="numeric"
                className="w-16 rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-1"
              />
              <button
                type="submit"
                title="Vazio repõe o limite por omissão do servidor."
                className="rounded border border-[var(--color-border)] px-1.5 hover:text-[var(--color-text)]"
              >
                Gravar
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  )
}
